import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { getInitials } from "../utils/util";
import moment from "moment";
import axios from "axios";
import { useSelector } from "react-redux";
import Select from "react-select";
import InfiniteScroll from "react-infinite-scroll-component";
import ConfirmBox from "./modals/ConfirmBox";
import apiClient from "../apiClient";
import { DELETE_MACHINE_RESPONSE, UPDATE_QUIZ_RESPONSES_PRIORITY } from "../apiClient/endpoints";
import { DeleteIcon, InfoIcon } from "../assets/Icons";

const SiteUserResponseList = ({ setResponseLenght, setUserResponse }) => {
  const { uuid } = useParams();
  const userData = useSelector((state) => state.userSlice);
  const configData = useSelector((state) => state.configSlice);
  const API_URL = configData.config.API_URL;
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const limit = 25;
  const [openConfirmBox, setOpenConfirmBox] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [dragEnabled, setDragEnabled] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [orderDirty, setOrderDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [savedOrder, setSavedOrder] = useState([]);
  const canManageResponses = userData?.user?.permissions?.includes("MACHINE.ADD");

  const fetchResponses = async (pageNumber = 1, isFilterChange = false) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/site/responses/list/${uuid}?page=${pageNumber}&limit=${limit}`,
        {
          ...(selectedSites?.length && { sites: selectedSites.map((e) => e.value) }),
        },
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const newResponses = data?.data;
      if (pageNumber === 1) {
        setResponseLenght(newResponses?.totalCount);
        const siteNames = [...new Set(newResponses?.list.map((e) => e?.site?.name).filter(Boolean))];
        if (!siteOptions.length) {
          setSiteOptions(
            siteNames.length > 0
              ? siteNames.map((name) => ({ label: name, value: name }))
              : [{ label: "No Option", value: "no-option" }],
          );
        }
      }

      if (isFilterChange || pageNumber === 1) {
        setResponses(newResponses?.list);
        setSavedOrder((newResponses?.list || []).map((item) => item.uuid));
        setOrderDirty(false);
        setDragEnabled(false);
        setDraggingId(null);
        setDragOverId(null);
        // setUserResponse(newResponses?.list);
      } else {
        setResponses((prev) => [...prev, ...newResponses?.list]);
        setSavedOrder((prev) => [...prev, ...(newResponses?.list || []).map((item) => item.uuid)]);
        // setUserResponse((prev) => [...prev, ...newResponses?.list]);
      }

      if (newResponses?.list.length < limit) {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Fetch error", error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllResponse = async () => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/site/responses/list/${uuid}`,
        {
          ...(selectedSites?.length && { sites: selectedSites.map((e) => e.value) }),
        },
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "application/json",
          },
        },
      );
      setUserResponse(data?.data?.list || []);
    } catch (error) {
      console.log(error, "error in fetching all response");
    }
  };

  const deleteResponse = async () => {
    try {
      await apiClient.post(`${API_URL}${DELETE_MACHINE_RESPONSE}/${selectedResponse}`);
    } catch (error) {
      console.log(error.message);
    } finally {
      fetchResponses();
    }
  };

  useEffect(() => {
    setLoading(true);
    setResponses([]);
    setPage(1);
    setHasMore(true);
    fetchResponses(1, true);
    fetchAllResponse();
  }, [uuid, selectedSites]);

  const fetchNext = () => {
    if (dragEnabled) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchResponses(nextPage);
  };

  const canDrag = canManageResponses && dragEnabled && !savingOrder;

  const savedOrderIndex = useMemo(() => {
    const map = new Map();
    savedOrder.forEach((id, idx) => map.set(id, idx));
    return map;
  }, [savedOrder]);

  const revertToSavedOrder = () => {
    setResponses((prev) => {
      if (!savedOrder || savedOrder.length === 0) return prev;
      const byId = new Map(prev.map((r) => [r.uuid, r]));
      const next = [];
      savedOrder.forEach((id) => {
        const row = byId.get(id);
        if (row) next.push(row);
      });
      prev.forEach((row) => {
        if (!savedOrderIndex.has(row.uuid)) next.push(row);
      });
      return next;
    });
    setOrderDirty(false);
    setDraggingId(null);
    setDragOverId(null);
  };

  const onToggleReorder = () => {
    setDragEnabled((prev) => {
      const next = !prev;
      if (prev === true && next === false) {
        if (orderDirty) revertToSavedOrder();
        setDraggingId(null);
        setDragOverId(null);
      }
      return next;
    });
  };

  const saveOrderData = async () => {
  try {
    setSavingOrder(true);
    const payload = responses.map((r, index) => ({
      uuid: r.uuid,
      priority: index + 1,
    }));

    await axios.post(
      `${API_URL}/api/site/update/responses-priority`,
      {
        responses: payload, 
      },
      {
        headers: {
          Authorization: `Bearer ${userData.token}`,
          "Content-Type": "application/json",
        },
      }
    );

    setSavedOrder(responses.map((r) => r.uuid));
    setOrderDirty(false);
    setDragEnabled(false);

    fetchResponses();
  } catch (error) {
    console.log("error", error);
  } finally {
    setSavingOrder(false);
    setDraggingId(null);
    setDragOverId(null);
  }
};
  const onDragStartRow = (id) => (e) => {
    if (!canDrag) return;
    setDraggingId(id);
    setDragOverId(null);
    try {
      e.dataTransfer.effectAllowed = "move";
    } catch (err) {
      // ignore
    }
  };

  const onDragOverRow = (id) => (e) => {
    if (!canDrag) return;
    e.preventDefault();
    if (dragOverId !== id) setDragOverId(id);
  };

  const onDropRow = (targetId) => (e) => {
    if (!canDrag) return;
    e.preventDefault();
    if (!draggingId || draggingId === targetId) {
      setDraggingId(null);
      setDragOverId(null);
      return;
    }

    setResponses((prev) => {
      const fromIndex = prev.findIndex((r) => r.uuid === draggingId);
      const toIndex = prev.findIndex((r) => r.uuid === targetId);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return prev;

      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    setOrderDirty(true);
    setDraggingId(null);
    setDragOverId(null);
  };

  const onDragEndRow = () => {
    setDraggingId(null);
    setDragOverId(null);
  };

  const canRearrangeResponses = responses.length >= 2;
  const rearrangeButtonTitle = canRearrangeResponses
    ? "Enable rearranging responses"
    : "Add more responses to rearrange";

  return (
    <>
      {loading ? (
        <div className="table-responsive workflowtb mt-2">
          <table className="table align-middle table-nowrap mb-0">{/* Skeleton UI */}</table>
        </div>
      ) : (
        <>
          {responses.length > 0 ? (
            <div className="checklist">
              <div className="row justify-content-end">
                <div className="col-md-12 d-flex align-items-center justify-content-end">
                  <div className="d-flex justify-content-end mb-2 selectSiteParent gap-2" style={{ width: "50%" }}>
                    
                  {/* {(selectedSites.length === 0) && canManageResponses && (
                      <>
                        {!dragEnabled ? (
                          <button
                            className="plain-btn icon"
                            onClick={onToggleReorder}
                            disabled={savingOrder || loading || !canRearrangeResponses}
                            title={rearrangeButtonTitle}
                          >
                            <span
                              className="reorder-btn-icon"
                              style={{ marginRight: "1.5px", marginBottom: "2px" }}
                              aria-hidden="true"
                            >
                              <span className="dot" />
                              <span className="dot" />
                              <span className="dot" />
                              <span className="dot" />
                              <span className="dot" />
                              <span className="dot" />
                            </span>
                            Rearrange Responses
                          </button>
                        ) : (
                          <>
                            <button
                              className="plain-btn secondary"
                              onClick={onToggleReorder}
                              disabled={savingOrder}
                              title="Cancel reordering"
                            >
                              Cancel
                            </button>
                            <button
                              className="plain-btn"
                              onClick={saveOrderData}
                              disabled={!orderDirty || savingOrder}
                              title={orderDirty ? "Save this order" : "Reorder rows to enable saving"}
                            >
                              {savingOrder ? "Saving…" : "Save Response Order"}
                            </button>
                          </>
                        )}
                      </>
                    )} */}
                    
                    <Select
                      value={selectedSites}
                      onChange={setSelectedSites}
                      options={siteOptions}
                      isMulti
                      placeholder="Select a site"
                    />
                  
                  </div>
                </div>
              </div>

              <div className="table-responsive workflowtb mt-2">
                <InfiniteScroll dataLength={responses.length} next={fetchNext} hasMore={hasMore && !dragEnabled}>
                  <table className="table align-middle table-nowrap mb-0">
                    <thead>
                      <tr>
                        <th className="text-center">S.No.</th>
                        <th>User</th>
                        <th>Designation</th>
                        <th>Site Name</th>
                        <th>Floor</th>
                        <th className="text-center">Date & Time</th>
                        <th className="text-center">Health Status</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {responses.map((response, index) => (
                        <tr
                          key={response.uuid}
                          draggable={canDrag}
                          onDragStart={onDragStartRow(response.uuid)}
                          onDragOver={onDragOverRow(response.uuid)}
                          onDrop={onDropRow(response.uuid)}
                          onDragEnd={onDragEndRow}
                          className={[
                            "checklist-row-draggable",
                            canDrag ? "is-enabled" : "is-disabled",
                            draggingId === response.uuid ? "is-dragging" : "",
                            dragOverId === response.uuid && draggingId && draggingId !== response.uuid ? "is-drag-over" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          <td className="text-center">
                            <div className="d-flex align-items-center justify-content-center">
                              <div className="form-check listchild">
                                <span className="form-check-label">
                                  {dragEnabled && canManageResponses && (
                                    <span className="checklist-drag-handle" title="Drag to reorder" aria-hidden="true">
                                      ⋮⋮
                                    </span>
                                  )}
                                  {index + 1}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="nameemail">
                              {response?.avatar ? (
                                <img
                                  src={response.avatar}
                                  alt={response.name || "Profile"}
                                  title={response.name || ""}
                                  className="profilepik"
                                  referrerPolicy="no-referrer"
                                />
                              ) : (
                                <div className="avatar-placeholder">{getInitials(response.name).toUpperCase()}</div>
                              )}
                              <span>
                                <p>{response.name}</p>
                                <p>{response.email}</p>
                              </span>
                            </div>
                          </td>
                          <td>{response?.designation || "N/A"}</td>
                          <td>{response?.site?.name}</td>
                          <td>{response?.metaData?.floor || "N/A"}</td>
                          <td>
                            <div className="datetime">
                              <p>{moment(response.createdAt).format("DD-MM-YYYY")}</p>
                              <p>{moment(response.createdAt).format("HH:mm")}</p>
                            </div>
                          </td>
                          <td className={`text-center ${response.health === 100 ? "good" : "bad"}`}>
                            <span>{Math.abs(response.health).toFixed(2)}%</span>
                          </td>
                          <td className="text-center">
                            <div className="action-btns">
                              <span className="eye" title="View details">
                                <Link to={`/site/user-response/${response.uuid}`} aria-label="View details">
                                  <InfoIcon />
                                </Link>
                              </span>
                              {userData?.user?.permissions?.includes("MACHINE.ADD") && (
                                <span
                                  title="Delete response"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    setSelectedResponse(response.uuid);
                                    setOpenConfirmBox(true);
                                  }}
                                >
                                  <DeleteIcon />
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </InfiniteScroll>
              </div>
            </div>
          ) : (
            <div className="row">
              <div className="px-4">
                <div className="first-event">No Response received yet!</div>
              </div>
            </div>
          )}
          <ConfirmBox
            isOpen={openConfirmBox}
            setIsOpen={setOpenConfirmBox}
            onClose={() => setSelectedResponse(null)}
            onSuccess={deleteResponse}
          />
        </>
      )}
    </>
  );
};

export default SiteUserResponseList;
